import { isMobile } from '../../core/device';

// BUG-P-7: Mobile-only button row for Replay / Prev / Next spectator target.
// Desktop users still rely on Arrow keys; mobile has no equivalent so we expose
// touch buttons inside the death-screen overlay.
export interface DeathScreenOptions {
  /** Invoked when the mobile "Replay" button is tapped. */
  onReplay?: () => void;
  /** Invoked when a mobile spectator nav button is tapped. */
  onSpectatorNav?: (direction: 'prev' | 'next') => void;
}

export class DeathScreen {
  private el: HTMLElement;
  private killedByEl: HTMLElement;
  private visible: boolean = false;
  private hideRequestId = 0;
  private pendingHideListener: ((event: TransitionEvent) => void) | null = null;
  private pendingHideTimeout: ReturnType<typeof setTimeout> | null = null;

  // BUG-P-7: Mobile control row. Built lazily on first show() call when
  // isMobile() is true. Stored so we can re-wire button handlers on subsequent
  // shows without rebuilding the DOM.
  private mobileButtonRow: HTMLElement | null = null;
  private replayBtn: HTMLButtonElement | null = null;
  private prevBtn: HTMLButtonElement | null = null;
  private nextBtn: HTMLButtonElement | null = null;
  private currentOnReplay: (() => void) | null = null;
  private currentOnSpectatorNav: ((direction: 'prev' | 'next') => void) | null = null;
  private mobileHandlersBound = false;

  constructor() {
    this.el = document.getElementById('death-screen-overlay')!;
    this.killedByEl = document.getElementById('killed-by-overlay')!;

    // Prepare for opacity transitions
    this.el.style.transition = 'opacity 0.3s ease-in';
    this.el.style.opacity = '0';
  }

  show(killerName: string, weaponName: string, options: DeathScreenOptions = {}): void {
    this.hideRequestId++;
    if (this.pendingHideListener) {
      this.el.removeEventListener('transitionend', this.pendingHideListener);
      this.pendingHideListener = null;
    }
    if (this.pendingHideTimeout) {
      clearTimeout(this.pendingHideTimeout);
      this.pendingHideTimeout = null;
    }
    this.killedByEl.textContent = `Killed by ${killerName} with ${weaponName}`;
    this.el.style.display = 'flex';

    // BUG-P-7: Update mobile button callbacks (only when on a mobile/tablet
    // device). The button row is lazily constructed on first mobile show().
    if (isMobile()) {
      this.ensureMobileButtons();
      this.currentOnReplay = options.onReplay ?? null;
      this.currentOnSpectatorNav = options.onSpectatorNav ?? null;
      this.updateMobileButtonAvailability();
    }

    // Trigger reflow so the transition plays
    void this.el.offsetHeight;
    this.el.style.opacity = '1';

    this.visible = true;
  }

  hide(): void {
    this.el.style.opacity = '0';
    const requestId = ++this.hideRequestId;

    if (this.pendingHideListener) {
      this.el.removeEventListener('transitionend', this.pendingHideListener);
    }
    if (this.pendingHideTimeout) {
      clearTimeout(this.pendingHideTimeout);
      this.pendingHideTimeout = null;
    }
    let completed = false;
    const completeHide = () => {
      if (completed || this.visible || requestId !== this.hideRequestId) return;
      completed = true;
      this.el.style.display = 'none';
      if (this.pendingHideListener) {
        this.el.removeEventListener('transitionend', this.pendingHideListener);
        this.pendingHideListener = null;
      }
      if (this.pendingHideTimeout) {
        clearTimeout(this.pendingHideTimeout);
        this.pendingHideTimeout = null;
      }
    };
    this.pendingHideListener = (event: TransitionEvent) => {
      if (event.target !== this.el) return;
      if (event.propertyName && event.propertyName !== 'opacity') return;
      completeHide();
    };
    this.el.addEventListener('transitionend', this.pendingHideListener, { once: true });
    this.pendingHideTimeout = setTimeout(completeHide, 350);

    this.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  // BUG-P-7: Build the mobile control row once and stash refs. Handlers are
  // bound once and dispatch to the current callback fields so we can swap
  // callbacks per show() without re-adding listeners.
  private ensureMobileButtons(): void {
    if (this.mobileButtonRow || typeof document === 'undefined') return;
    if (typeof document.createElement !== 'function') return;

    const row = document.createElement('div');
    row.id = 'death-screen-mobile-controls';
    row.style.cssText = [
      'display:flex',
      'gap:12px',
      'margin-top:24px',
      'flex-wrap:wrap',
      'justify-content:center',
      "font-family:'Courier New',monospace",
    ].join(';');

    this.replayBtn = this.makeButton('Replay', 'death-screen-replay');
    this.prevBtn = this.makeButton('< Prev Target', 'death-screen-prev');
    this.nextBtn = this.makeButton('Next Target >', 'death-screen-next');

    row.appendChild(this.replayBtn);
    row.appendChild(this.prevBtn);
    row.appendChild(this.nextBtn);

    // Bind handlers once. They read from this.currentOnReplay / Nav so we can
    // refresh callbacks between deaths without re-binding.
    if (!this.mobileHandlersBound) {
      this.replayBtn.addEventListener('click', this.handleReplayClick);
      this.prevBtn.addEventListener('click', this.handlePrevClick);
      this.nextBtn.addEventListener('click', this.handleNextClick);
      this.mobileHandlersBound = true;
    }

    if (typeof this.el.appendChild === 'function') {
      this.el.appendChild(row);
    }
    this.mobileButtonRow = row;
  }

  private makeButton(label: string, id: string): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.id = id;
    btn.type = 'button';
    btn.textContent = label;
    btn.style.cssText = [
      'min-width:120px',
      'min-height:48px',
      'padding:10px 16px',
      'font-size:16px',
      "font-family:'Courier New',monospace",
      'color:#fff',
      'background:rgba(20,20,20,0.85)',
      'border:1px solid #ff4444',
      'border-radius:6px',
      'cursor:pointer',
      'touch-action:manipulation',
    ].join(';');
    return btn;
  }

  // Update disabled state to reflect whether a callback is wired.
  private updateMobileButtonAvailability(): void {
    if (this.replayBtn) {
      this.replayBtn.disabled = !this.currentOnReplay;
      this.replayBtn.style.opacity = this.currentOnReplay ? '1' : '0.4';
    }
    const navOn = !!this.currentOnSpectatorNav;
    if (this.prevBtn) {
      this.prevBtn.disabled = !navOn;
      this.prevBtn.style.opacity = navOn ? '1' : '0.4';
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = !navOn;
      this.nextBtn.style.opacity = navOn ? '1' : '0.4';
    }
  }

  private handleReplayClick = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
    this.currentOnReplay?.();
  };

  private handlePrevClick = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
    this.currentOnSpectatorNav?.('prev');
  };

  private handleNextClick = (event: Event): void => {
    event.preventDefault();
    event.stopPropagation();
    this.currentOnSpectatorNav?.('next');
  };
}
